import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Fetch exchanges with proper pagination
export const fetchExchanges = async ({
  PageNumber = 1,
  Size = 10,
  Search = '',
  Status = '',
  DateFrom = '',
  DateTo = '',
  Syncro = ''
}) => {
  try {
    // Ensure PageNumber and Size are numbers
    const pageNumber = Number(PageNumber);
    const pageSize = Number(Size);
    
    // Build query params manually to ensure correct formatting
    let url = `${API_URL}/BonEchange/Pagination?PageNumber=${pageNumber}&Size=${pageSize}`;
    
    if (Search) url += `&Search=${encodeURIComponent(Search)}`;
    if (Status) url += `&Status=${encodeURIComponent(Status)}`;
    if (DateFrom) url += `&DateFrom=${encodeURIComponent(DateFrom)}`;
    if (DateTo) url += `&DateTo=${encodeURIComponent(DateTo)}`;
    if (Syncro) url += `&Syncro=${encodeURIComponent(Syncro)}`;
    
    console.log('Fetching exchanges with URL:', url);
    
    const response = await axios.get(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    console.log('Exchange API response:', response.data);
    
    // Format response to match expected structure - updated to match actual API response
    return {
      data: response.data.list || [], // Changed from items to list
      currentPage: response.data.page || 1, // Changed from pageNumber to page
      pageSize: response.data.pageSize || 10,
      totalItems: response.data.total || 0, // Changed from totalCount to total
      totalPages: response.data.maxPage || 1 // Changed from totalPages to maxPage
    };
  } catch (error) {
    console.error('Error fetching exchanges:', error);
    throw error;
  }
};

// Fetch exchange by ID - adding the missing function
export const fetchExchangeById = async (id) => {
  try {
    if (!id) {
      throw new Error('Exchange ID is required');
    }
    
    const response = await axios.get(`${API_URL}/BonEchange/byid/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    // Log the response for debugging
    console.log('Exchange detail API response:', response.data);
    
    if (!response.data) {
      throw new Error('Exchange not found');
    }
    
    // The API returns data in { entet, line } format
    // We need to format it to match what the edit page expects
    const exchangeData = {
      // Main exchange details from entet
      ...response.data.entet,
      // Add the line items
      items: response.data.line.map(item => ({
        id: item.id,
        productId: item.productId,
        productVariantId: item.productVariantId,
        product: item.product,
        productVariant: item.productVariant,
        quantite: item.quantite,
        prixUnitaire: item.prixUnitaire,
        totalTTC: item.totalTTC,
        tauxTva: item.tauxTva,
        totalHt: item.totalHt,
        totalTva: item.totalTva,
        totalRemise: item.totalRemise,
        tauxRemise: item.tauxRemise,
        aEchange: item.aEchange,
      }))
    };
    
    console.log('Formatted exchange data for edit page:', exchangeData);
    
    return exchangeData;
  } catch (error) {
    console.error(`Error fetching exchange with ID ${id}:`, error);
    throw error;
  }
};

// Fetch exchange statistics
export const fetchExchangeStats = async () => {
  try {
    const response = await axios.get(`${API_URL}/BonEchange/Header`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    console.log('Exchange stats API response:', response.data);
    
    // Return the original API response fields AND our mapped fields
    // This gives us flexibility in how we handle the display
    return {
      // Original API response fields
      tous: response.data.tous || 0,
      pannnierAbondonner: response.data.pannnierAbondonner || 0,
      enAttente: response.data.enAttente || 0,
      confirmees: response.data.confirmees || 0,
      pretAExpedier: response.data.pretAExpedier || 0,
      expedier: response.data.expedier || 0,
      livrer: response.data.livrer || 0,
      anuller: response.data.anuller || 0,
      paid: response.data.paid || 0,
      return: response.data.return || 0,
      returnReceived: response.data.returnReceived || 0,
      
      // Mapped fields for consistent naming in our app
      total: response.data.tous || 0,
      pending: response.data.enAttente || 0,
      processing: response.data.pretAExpedier || 0,
      completed: response.data.livrer || 0,
      cancelled: response.data.anuller || 0,
      confirmed: response.data.confirmees || 0,
      shipped: response.data.expedier || 0,
      returned: response.data.return || 0
    };
  } catch (error) {
    console.error('Error fetching exchange stats:', error);
    // Return default values for both original and mapped fields
    return {
      // Original API fields with default values
      tous: 0,
      pannnierAbondonner: 0,
      enAttente: 0,
      confirmees: 0,
      pretAExpedier: 0,
      expedier: 0,
      livrer: 0,
      anuller: 0,
      paid: 0,
      return: 0,
      returnReceived: 0,
      
      // Mapped fields with default values
      total: 0,
      pending: 0,
      processing: 0,
      completed: 0,
      cancelled: 0,
      confirmed: 0,
      shipped: 0,
      returned: 0
    };
  }
};

// Add a new exchange
export const addExchange = async (exchangeData) => {
  try {
    const response = await axios.post(`${API_URL}/BonEchange/save`, exchangeData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error adding exchange:', error);
    throw error;
  }
};

// Update an exchange
export const updateExchange = async (exchangeData) => {
  try {
    if (!exchangeData.id) {
      throw new Error('Exchange ID is required for updates');
    }
    
    console.log('Sending update exchange request with data:', exchangeData);
    
    const response = await axios.put(`${API_URL}/BonEchange/${exchangeData.id}`, exchangeData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    
    console.log('Exchange update response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating exchange:', error);
    throw error.response?.data?.message || error.message || 'Failed to update exchange';
  }
};

// Combined save function - adding the missing function
export const saveExchange = async (exchangeData) => {
  try {
    // Determine if this is a new exchange or an existing one being updated
    const isUpdate = !!exchangeData.id;
    
    if (isUpdate) {
      // Update existing exchange
      const id = exchangeData.id;
      const response = await axios.put(`${API_URL}/BonEchange/${id}`, exchangeData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } else {
      // Create new exchange
      const response = await axios.post(`${API_URL}/BonEchange/save`, exchangeData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    }
  } catch (error) {
    console.error('Error saving exchange:', error);
    throw error;
  }
};

// Delete an exchange
export const deleteExchange = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/BonEchange/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting exchange:', error);
    throw error;
  }
};

// Change exchange status
export const changeExchangeStatus = async (id, etat) => {
  try {
    const response = await axios.put(
      `${API_URL}/BonEchange/ChangerEtat?etat=${encodeURIComponent(etat)}&Id=${encodeURIComponent(id)}`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error changing exchange status:', error);
    throw error;
  }
};

// Add sync and tracking functions to the exchange controller

// Synchronize exchanges with external systems
export const syncronizeExchanges = async (exchangeIds) => {
  try {
    const response = await axios.patch(
      `${API_URL}/BonEchange/Syncronize`,
      exchangeIds,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error synchronizing exchanges:', error);
    throw error;
  }
};

// Update tracking information for exchanges
export const trackExchanges = async (exchangeIds) => {
  try {
    const response = await axios.patch(
      `${API_URL}/BonEchange/Tracking`,
      exchangeIds,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error tracking exchanges:', error);
    throw error;
  }
};

